import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import { GridColumns, GridColumnVisibilityModel, GridSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useState } from 'react';
import { selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { useDispatch, useSelector } from 'react-redux';
import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import _ from 'lodash';
import { globalSearchActions, totalCountErrorThreshold } from 'store/redux-store/global-search/slice';
import { usePromiseTracker } from 'react-promise-tracker';
import { useTranslation } from 'react-i18next';
import { getFilterRequest } from '@AMIEWEB/GlobalSearch/helper';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { selectSearchResults } from 'store/redux-store/global-search/selectors';
import { SearchOrderGridColumnVisibilityModel, OrdersSearchGridViewInfo } from './OrdersSearchGridUtils';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { selectPlacementDetails, selectPlacementHeaderData } from 'store/redux-store/placement-details/selectors';

export const OrdersSearchGrid = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const gridState = useSelector(selectGridState);
  const [currentColumns] = useState<GridColumns>([...OrdersSearchGridViewInfo()]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    _.cloneDeep(SearchOrderGridColumnVisibilityModel),
  );
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const { setSaveDisabled, skillSetSelectionDialog, setSkillSetSelectionDialog } = props;
  const { promiseInProgress } = usePromiseTracker({ area: PromiseTrackerKeys.global.searchCall, delay: 0 });
  const [defaultSortModel] = React.useState<any>({ column: 'orderId', direction: 'desc' });
  const { items: rows, count } = useSelector(selectSearchResults);
  const placementDetails = useSelector(selectPlacementDetails);
  const placementHeaderDetails = useSelector(selectPlacementHeaderData);

  const handleFetchData = React.useCallback(
    (size: number, number: number, sortedColumn?: { column: string; direction: any } | undefined | null) => {
      if (gridState?.filterAttributes?.filters) {
        dispatch(
          globalSearchActions.getSearchResults({
            searchType: SearchType.order,
            keyword: '',
            pageNumber: number,
            pageSize: size,
            sortedColumn: sortedColumn,
            filter: gridState?.filterAttributes?.filters
              ? getFilterRequest(Object.fromEntries(gridState?.filterAttributes?.filters))
              : {},
            translation: t,
          }),
        );
        setSaveDisabled(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridState?.filterAttributes?.filters],
  );

  const onSortModelChanged = (model: GridSortModel) => {
    const pagination = apiRef?.current?.state?.pagination;
    if (pagination) {
      handleFetchData(
        pagination.pageSize,
        pagination.page + 1,
        model.length > 0
          ? {
              column: model[0].field,
              direction: model[0].sort,
            }
          : undefined,
      );
    }
  };

  const onPageChange = (page: number) => {
    const pagination = apiRef?.current?.state?.pagination;
    if (pagination) handleFetchData(pagination.pageSize, page + 1, gridState?.sortedColumn ?? defaultSortModel);
  };

  const onPageSizeChange = (pageSize: number) => {
    if (gridState.pageSize !== pageSize) handleFetchData(pageSize, 1, gridState?.sortedColumn ?? defaultSortModel);
  };

  const onRowsSelectionHandler = row => {
    if (row) {
      const allSkillSets = row[0]?.skillsets ? _.split(row[0]?.skillsets, ',') : [];
      if (allSkillSets?.length > 1 && placementHeaderDetails?.brandId === 10) {
        const options = _.map(allSkillSets, function (item, index) {
          const eachSkillSet = _.split(_.trim(item), '-');
          if (eachSkillSet?.length > 1) {
            const disc = placementDetails?.disciplineData?.find(x => x.abbreviation === eachSkillSet[0]);
            const spec = placementDetails?.specialitiesData?.find(
              x => x.description === eachSkillSet[1] && x.disciplineId === disc?.id,
            );
            return {
              ID: index,
              Description: _.trim(item),
              DisciplineId: spec?.disciplineId,
              SpecialtyId: spec?.specialtyId,
            };
          }
        });

        setSkillSetSelectionDialog({
          ...skillSetSelectionDialog,
          orderId: row[0]?.orderId,
          skillSetSelectionOptions: options,
          selectedSkillSet: null,
        });
      } else {
        setSkillSetSelectionDialog({
          ...skillSetSelectionDialog,
          orderId: row[0]?.orderId,
          skillSetSelectionOptions: [],
          selectedSkillSet: {
            DisciplineId: 0,
            SpecialtyId: 0,
          },
        });
      }
      setSaveDisabled(false);
    }
  };

  return (
    <>
      <LayoutGrid>
        <LayoutGridItem xs={12}>
          <Box>
            <XGrid
              title={'Orders'}
              apiRef={apiRef}
              columns={currentColumns}
              columnVisibilityModel={columnVisibilityModel}
              rows={rows || []}
              inDialog
              height={450}
              rowsPerPageOptions={[250, 300, 400, 500]}
              defaultRowsPerPage={250}
              paginationMode="server"
              sortingMode="server"
              onSortModelChange={model => onSortModelChanged(model)}
              disableMultipleSelection
              loading={promiseInProgress}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              rowCount={count > totalCountErrorThreshold ? totalCountErrorThreshold : count}
              selectionModel={selectionModel}
              onSelectionModelChange={(model: GridSelectionModel) => {
                setSelectionModel(model);
                let data = rows && model.length > 0 ? rows.filter(x => x.id === model[0]?.toString()) : null;
                onRowsSelectionHandler(data);
              }}
              onColumnVisibilityModelChange={model => {
                setColumnVisibilityModel(_.cloneDeep(model));
              }}
            />
          </Box>
        </LayoutGridItem>
      </LayoutGrid>
    </>
  );
};
